import { z } from 'zod';

import { isServer } from '@shared-web/utils/runtime';

export type SamplerType =
  | 'always_on'
  | 'always_off'
  | 'traceidratio'
  | 'parentbased_always_on'
  | 'parentbased_always_off'
  | 'parentbased_traceidratio';

export const SAMPLER_CONFIG: Record<
  string,
  { type: SamplerType; arg: number }
> = {
  development: { type: 'always_on', arg: 1 },
  staging: { type: 'parentbased_traceidratio', arg: 0.5 },
  dstg: { type: 'parentbased_traceidratio', arg: 0.5 },
  production: { type: 'parentbased_traceidratio', arg: 0.1 },
};

export function getSamplerConfig(environment: string): {
  type: SamplerType;
  arg: number;
} {
  const fallback = SAMPLER_CONFIG['production'];
  if (!fallback) {
    throw new Error('Production sampler config is required');
  }
  return SAMPLER_CONFIG[environment] ?? fallback;
}

export function getDeviceType(): string {
  if (isServer()) return 'Unknown';

  const width = window.innerWidth;
  if (width < 768) return 'Mobile';
  if (width < 1024) return 'Tablet';
  return 'Desktop';
}

// Base telemetry configuration schema
const BaseTelemetryConfigSchema = z.object({
  serviceName: z.string().min(1, 'Service name is required'),
  serviceVersion: z.string().min(1, 'Service version is required'),
  otlpEndpoint: z
    .string()
    .url('OTLP endpoint must be a valid URL')
    .or(z.string().startsWith('/', 'OTLP endpoint must be a valid path')),
  logsEndpoint: z
    .string()
    .url('Logs endpoint must be a valid URL')
    .or(z.string().startsWith('/', 'Logs endpoint must be a valid path'))
    .optional(),
  metricsEndpoint: z
    .string()
    .url('Metrics endpoint must be a valid URL')
    .or(z.string().startsWith('/', 'Metrics endpoint must be a valid path')),
  accessToken: z.string().optional(),
  environment: z.string().min(1, 'Environment is required'),
  debug: z.boolean(),
});

/**
 * Schema for sampling rules - allows consumers to define URL-based sampling.
 */
const SamplingRuleSchema = z.object({
  pattern: z.instanceof(RegExp),
  rate: z.number().min(0).max(1),
  name: z.string(),
});

// Client-specific configuration schema
export const ClientTelemetryConfigSchema = BaseTelemetryConfigSchema.extend({
  deviceType: z.string().optional(),
  isSyntheticRequest: z.boolean().optional(),
  enableConsoleLogging: z.boolean().optional(),
  /**
   * Custom sampling rules for URL-based adaptive sampling.
   * Rules are evaluated in order; first match wins.
   * Built-in rules (health checks, static assets) are automatically appended.
   *
   * @example
   * ```typescript
   * samplingRules: [
   *   { pattern: /\/api\/order/, rate: 1.0, name: 'orders' },
   *   { pattern: /\/api\/payment/, rate: 1.0, name: 'payments' },
   * ]
   * ```
   */
  samplingRules: z.array(SamplingRuleSchema).optional(),
  /**
   * Whether to use AdaptiveSampler instead of the default environment-based sampler.
   * When true, uses URL-based sampling with the provided samplingRules.
   */
  useAdaptiveSampling: z.boolean().optional(),
});

// Server-specific configuration schema
export const ServerTelemetryConfigSchema = BaseTelemetryConfigSchema.extend({
  enableAutoInstrumentation: z.boolean().optional(),
  enableConsoleExporter: z.boolean().optional(),
  exportTimeoutMillis: z.number().optional(),
  maxQueueSize: z.number().optional(),
  maxExportBatchSize: z.number().optional(),
  scheduledDelayMillis: z.number().optional(),
});

export const ClientTelemetryConfigOptionsSchema =
  ClientTelemetryConfigSchema.partial();
export const ServerTelemetryConfigOptionsSchema =
  ServerTelemetryConfigSchema.partial();

export type ClientTelemetryConfig = z.infer<typeof ClientTelemetryConfigSchema>;
export type ServerTelemetryConfig = z.infer<typeof ServerTelemetryConfigSchema>;
export type ClientTelemetryConfigOptions = z.infer<
  typeof ClientTelemetryConfigOptionsSchema
>;
export type ServerTelemetryConfigOptions = z.infer<
  typeof ServerTelemetryConfigOptionsSchema
>;

// Create client configuration
export function createClientTelemetryConfig(
  options: ClientTelemetryConfigOptions = {},
): ClientTelemetryConfig {
  const config: ClientTelemetryConfig = {
    serviceName: options.serviceName || 'x-web-frontend',
    serviceVersion: options.serviceVersion || '1.0.0',
    otlpEndpoint: options.otlpEndpoint || '',
    logsEndpoint: options.logsEndpoint || options.otlpEndpoint,
    metricsEndpoint:
      options.metricsEndpoint || options.otlpEndpoint || '/api/metrics',
    accessToken: options.accessToken,
    environment: options.environment || 'development',
    debug: options.debug || false,
    deviceType: options.deviceType || getDeviceType(),
    isSyntheticRequest: options.isSyntheticRequest || false,
    enableConsoleLogging: options.enableConsoleLogging ?? false,
    samplingRules: options.samplingRules,
    useAdaptiveSampling: options.useAdaptiveSampling ?? false,
  };

  if (config.debug) {
    console.log('[OpenTelemetry] Client Configuration:', {
      ...config,
      accessToken: config.accessToken ? '***' : undefined,
      samplingRules: config.samplingRules?.length
        ? `${config.samplingRules.length} rules`
        : undefined,
    });
  }

  return config;
}

// Create server configuration
export function createServerTelemetryConfig(
  options: ServerTelemetryConfigOptions = {},
): ServerTelemetryConfig {
  const config: ServerTelemetryConfig = {
    serviceName: options.serviceName || 'x-web-server',
    serviceVersion: options.serviceVersion || '1.0.0',
    otlpEndpoint: options.otlpEndpoint || '',
    logsEndpoint:
      options.logsEndpoint ||
      options.otlpEndpoint ||
      'https://httpbin.org/post',
    metricsEndpoint:
      options.metricsEndpoint || options.otlpEndpoint || '/api/metrics',
    accessToken: options.accessToken,
    environment: options.environment || 'development',
    debug: options.debug || false,
    enableAutoInstrumentation: options.enableAutoInstrumentation ?? true,
    enableConsoleExporter: options.enableConsoleExporter ?? false,
    exportTimeoutMillis: options.exportTimeoutMillis || 30000,
    maxQueueSize: options.maxQueueSize || 100,
    maxExportBatchSize: options.maxExportBatchSize || 50,
    scheduledDelayMillis: options.scheduledDelayMillis || 5000,
  };

  if (config.debug) {
    console.log('[OpenTelemetry] Server Configuration:', {
      ...config,
      accessToken: config.accessToken ? '***' : undefined,
    });
  }

  return config;
}

// Validate configuration
export function validateTelemetryConfig<
  T extends ClientTelemetryConfig | ServerTelemetryConfig,
>(config: unknown, isServer = false): config is T {
  const schema = isServer
    ? ServerTelemetryConfigSchema
    : ClientTelemetryConfigSchema;
  const result = schema.safeParse(config);

  if (!result.success) {
    console.warn(
      '[OpenTelemetry] Configuration validation failed:',
      result.error.issues
        .map((e: z.ZodIssue) => `${e.path.join('.')}: ${e.message}`)
        .join(', '),
    );
    return false;
  }

  return true;
}
